import { GRID_CONFIG, sortByAuditUpdatedOn } from "../config";
import axios from "axios";
import React, { useState, useEffect, useRef } from "react";
import DataTable from "react-data-table-component";
import {
    FaPencilAlt,
    FaTrash,
    FaEllipsisV,
    FaPlusCircle,
    FaSearch,
    FaCheckCircle,
    FaTimesCircle,
    FaAngleDoubleLeft,
    FaAngleLeft,
    FaAngleRight,
    FaAngleDoubleRight,
} from "react-icons/fa";
import "react-toastify/dist/ReactToastify.css";
import { io } from "socket.io-client";
import Swal from "sweetalert2";
import { notifyUser } from "../utils/notifyUser";

const socket = io(process.env.REACT_APP_API_SOCKET_URL);
const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api/configs`;
// console.log("API_URL", API_URL);

const ConfigurationSettings = () => {
    const [configs, setConfigs] = useState([]);
    const [filteredConfigs, setFilteredConfigs] = useState([]);
    const [selectedConfig, setSelectedConfig] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [form, setForm] = useState({
        field_name: "",
        field_value: "",
        var_data: "",
        is_enabled: 1,
    });
    const [searchTerm, setSearchTerm] = useState("");
    const [perPage, setPerPage] = useState(GRID_CONFIG.defaultPageSize);
    const [currentPage, setCurrentPage] = useState(1);
    const actionRef = useRef(null);
    const [, setShowActions] = useState(false);

    useEffect(() => {
        fetchConfigs();
    }, []);

    useEffect(() => {
        const handleConfigUpdate = () => {
            console.log(
                ":arrows_counterclockwise: Reloading config table due to WebSocket event",
            );
            fetchConfigs();
        };
        socket.on("config_updated", handleConfigUpdate);
        return () => {
            socket.off("config_updated", handleConfigUpdate);
        };
    }, []);

    const fetchConfigs = async () => {
        try {
            const res = await axios.get(API_URL, { withCredentials: true });
            setConfigs(sortByAuditUpdatedOn(res.data));
        } catch{
            notifyUser(null, "error", "Failed to fetch config settings.", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    useEffect(() => {
        filterAndPaginate();
    }, [configs, searchTerm, perPage, currentPage]);

    const filterAndPaginate = () => {
        const lower = searchTerm.toLowerCase();
        const filtered = configs.filter(
            (cfg) =>
                cfg.field_name?.toLowerCase().includes(lower) ||
                cfg.field_value?.toLowerCase().includes(lower) ||
                cfg.var_data?.toLowerCase().includes(lower),
        );

        const startIndex = (currentPage - 1) * perPage;
        const paginated = filtered.slice(startIndex, startIndex + perPage);

        setFilteredConfigs(paginated);
    };

    const totalPages = Math.ceil(
        configs.filter(
            (cfg) =>
                cfg.field_name
                    ?.toLowerCase()
                    .includes(searchTerm.toLowerCase()) ||
                cfg.field_value
                    ?.toLowerCase()
                    .includes(searchTerm.toLowerCase()) ||
                cfg.var_data?.toLowerCase().includes(searchTerm.toLowerCase()),
        ).length / perPage,
    );

    const handleRowClick = (row) => {
        setSelectedConfig(row.id === selectedConfig?.id ? null : row);
        setShowActions(false);
    };

    const handleEdit = () => {
        setForm(selectedConfig);
        setShowModal(true);
    };

    const handleDelete = async () => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: `This will delete "${selectedConfig.field_name}"`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, delete it!",
        });

        if(result.isConfirmed) {
            try {
                await axios.delete(`${API_URL}/${selectedConfig.id}`, {
                    withCredentials: true,
                });
                notifyUser(null, "success", "Config deleted.", "toast", null, null, null, null, null, false, false, "top-center");
                setSelectedConfig(null);
                fetchConfigs();
            } catch{
                notifyUser(null, "error", "Failed to delete.", "toast", null, null, null, null, null, false, false, "top-center");
            }
        }
    };

    const handleCreate = () => {
        setForm({
            field_name: "",
            field_value: "",
            var_data: "",
            is_enabled: 1,
        });
        setShowModal(true);
    };

    const handleChange = (e) => {
        const { name, value, checked } = e.target;

        if(name === "is_enabled") {
            const newForm = {
                ...form,
                is_enabled: checked ? 1 : 0,
            };

            // Special logic: also update `field_value` for boolean-type flags
            if(
                form.field_name === "GOOGLE_AUTH_ENABLED" ||
                form.field_name === "MICROSOFT_AUTH_ENABLED"
            ) {
                newForm.field_value = checked ? "true" : "false";
            }

            setForm(newForm);
        } else {
            setForm({ ...form, [name]: value });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const payload = {
            ...form,
            audit_created_by: "admin",
            audit_updated_by: "admin",
        };

        try {
            if(form.id) {
                await axios.put(`${API_URL}/${form.id}`, payload, {
                    withCredentials: true,
                });
                notifyUser(null, "success", "Updated.", "toast", null, null, null, null, null, false, false, "top-center");
            } else {
                await axios.post(API_URL, payload, { withCredentials: true });
                notifyUser(null, "success", "Created.", "toast", null, null, null, null, null, false, false, "top-center");
            }
            setShowModal(false);
            fetchConfigs();
        } catch{
            notifyUser(null, "error", "Failed to save.", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    useEffect(() => {
        const closeOnClickOutside = (e) => {
            if(actionRef.current && !actionRef.current.contains(e.target)) {
                setShowActions(false);
            }
        };
        document.addEventListener("mousedown", closeOnClickOutside);
        return () =>
            document.removeEventListener("mousedown", closeOnClickOutside);
    }, []);

    const columns = [
        { name: "Key", selector: (row) => row.field_name, sortable: true },
        {
            name: "Value",
            selector: (row) => row.field_value,
            sortable: true,
            cell: (row) => (
                <div
                    style={{
                        maxWidth: "250px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                    title={row.field_value}
                >
                    {row.field_value}
                </div>
            ),
        },
        {
            name: "Description",
            selector: (row) => row.var_data,
            sortable: true,
            cell: (row) => (
                <div
                    style={{
                        maxWidth: "250px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                    title={row.var_data}
                >
                    {row.var_data}
                </div>
            ),
        },
        {
            name: "Enabled",
            selector: (row) =>
                row.is_enabled ? (
                    <FaCheckCircle
                        style={{
                            color: "green",
                            width: "20px",
                            height: "20px",
                        }}
                    />
                ) : (
                    <FaTimesCircle
                        style={{ color: "red", width: "20px", height: "20px" }}
                    />
                ),
            sortable: true,
            maxWidth: "100px",
        },
        {
            name: " Created By",
            selector: (row) => row.audit_created_by,
            sortable: true,
        },
        {
            name: " Created On",
            selector: (row) => row.audit_created_on,
            sortable: true,
        },
        {
            name: " Updated By",
            selector: (row) => row.audit_updated_by,
            sortable: true,
        },
        {
            name: " Updated On",
            selector: (row) => row.audit_updated_on,
            sortable: true,
        },
    ];

    const customStyles = {
        table: {
            style: {
                borderRadius: "8px",
                overflow: "hidden",
            },
        },
        headRow: {
            style: {
                backgroundColor: "var(--navy)",
                color: "white",
                fontWeight: "bold",
            },
        },
        rows: {
            style: {
                cursor: "pointer",
                "&:hover": { backgroundColor: "#f1f8ff" },
            },
        },
    };

    const handleExportConfigsCSV = () => {
        try {
            const filtered = configs.filter(
                (cfg) =>
                    cfg.field_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    cfg.field_value?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    cfg.var_data?.toLowerCase().includes(searchTerm.toLowerCase()),
            );

            if (!filtered || filtered.length === 0) {
                notifyUser(null, "info", "No configuration items to export", "toast", null, null, null, null, null, false, false, "top-center");
                return;
            }

            const rows = filtered.map((c) => ({
                Key: c.field_name || "",
                Value: c.field_value || "",
                Description: c.var_data || "",
                Enabled: c.is_enabled ? "true" : "false",
                CreatedBy: c.audit_created_by || "",
                CreatedOn: c.audit_created_on || "",
                UpdatedBy: c.audit_updated_by || "",
                UpdatedOn: c.audit_updated_on || "",
            }));

            const header = Object.keys(rows[0]).join(",");
            const csvBody = rows
                .map((r) =>
                    Object.values(r)
                        .map((v) => `"${String(v).replace(/\"/g, '""')}"`)
                        .join(","),
                )
                .join("\n");

            const csv = `${header}\n${csvBody}`;
            const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
                "download",
                `configs-${new Date().toISOString().slice(0, 10)}.csv`,
            );
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            notifyUser(null, "error", "Failed to export configurations", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    return (
        <div
            className="aqi-grid-page"
            style={{
                padding: "30px",
                width: "90%",
                margin: "30px auto",
                boxShadow: "0 0 20px rgba(0,0,0,0.1)",
                borderRadius: "8px",
                backgroundColor: "white",
                position: "relative",
            }}
        >
            <h2
                style={{
                    color: "var(--navy)",
                    marginBottom: "20px",
                    fontSize: "large",
                    fontFamily: "large",
                }}
            >
                CONFIGURATION SETTINGS
            </h2>
            <div className="aqi-grid-toolbar">
                <div>
                    <select
                        style={{
                            padding: "0.375rem 2rem 0.375rem 0.75rem",
                            color: "var(--navy)",
                            backgroundColor: "#F5F7FA",
                            border: "2px solid #C2D7E2",
                            borderRadius: "0.375rem",
                            fontWeight: "500",
                            appearance: "none",
                            backgroundImage:
                                "var(--navy-select-chevron)",
                            backgroundRepeat: "no-repeat",
                            backgroundPosition: "right 0.5rem center",
                            backgroundSize: "16px 12px",
                            transition: "all 0.2s ease",
                            cursor: "pointer",
                            outline: "none",
                        }}
                        className="aqi-grid-control hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                        value={perPage}
                        onChange={(e) => {
                            setPerPage(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                    >
                        {GRID_CONFIG.pageSizeOptions.map((option) => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                </div>

                <div className="aqi-grid-search">
                        <FaSearch
                            className="absolute left-3 top-2.5"
                            style={{ color: "var(--navy)" }}
                        />
                        <input
                            type="text"
                            placeholder="Search configs..."
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1);
                            }}
                            style={{
                                paddingLeft: "2.5rem",
                                paddingRight: "0.75rem",
                                paddingTop: "0.5rem",
                                paddingBottom: "0.5rem",
                                borderRadius: "0.375rem",
                                border: "2px solid #C2D7E2",
                                backgroundColor: "#F5F7FA",
                                transition: "all 0.2s ease",
                                outline: "none",
                                color: "var(--navy)",
                            }}
                            className="aqi-grid-search-input hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                        />
                </div>
                <div className="aqi-grid-toolbar-actions aqi-grid-toolbar-actions--export-last">
                    <button
                        className="aqi-grid-button aqi-grid-button--primary"
                        onClick={handleCreate}
                    >
                        <FaPlusCircle /> Create
                    </button>
                    {selectedConfig && (
                        <>
                            <button
                                className="aqi-grid-button aqi-grid-button--primary"
                                onClick={handleEdit}
                            >
                                <FaPencilAlt /> Edit
                            </button>
                            <button
                                className="aqi-grid-button aqi-grid-button--danger"
                                onClick={handleDelete}
                            >
                                <FaTrash /> Delete
                            </button>
                            <button
                                className="aqi-grid-button aqi-grid-button--warning"
                                onClick={() => notifyUser(null, "info", "More actions coming soon", "toast", null, null, null, null, null, false, false, "top-center")}
                            >
                                <FaEllipsisV /> Actions
                            </button>
                        </>
                    )}
                    <button
                        onClick={handleExportConfigsCSV}
                        title="Export CSV"
                        className="aqi-grid-button aqi-grid-button--primary"
                    >
                        Export
                    </button>
                </div>
            </div>

            {/* Table */}
            <DataTable
                columns={columns}
                data={filteredConfigs}
                highlightOnHover
                pointerOnHover
                customStyles={customStyles}
                striped
                onRowClicked={handleRowClick}
                pagination={false}
                conditionalRowStyles={[
                    {
                        when: (row) =>
                            selectedConfig && row.id === selectedConfig.id,
                        style: {
                            backgroundColor: "#f0f0f0",
                            color: "#333",
                            fontWeight: "bold",
                        },
                    },
                ]}
            />

            {/* Pagination */}
            <div className="flex justify-between items-center px-2 py-3 text-sm text-gray-700 mt-4">
                <span>
                    Showing{" "}
                    {configs.length === 0 ? 0 : (currentPage - 1) * perPage + 1}{" "}
                    to {Math.min(currentPage * perPage, configs.length)} of{" "}
                    {configs.length} records
                </span>
                <div className="flex gap-2 text-lg items-center">
                    <button
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(1)}
                        className="disabled:opacity-40"
                    >
                        <FaAngleDoubleLeft />
                    </button>
                    <button
                        disabled={currentPage === 1}
                        onClick={() =>
                            setCurrentPage((prev) => Math.max(prev - 1, 1))
                        }
                        className="disabled:opacity-40"
                    >
                        <FaAngleLeft />
                    </button>
                    <span className="text-sm px-2">
                        Page {currentPage} of {totalPages}
                    </span>
                    <button
                        disabled={currentPage === totalPages}
                        onClick={() =>
                            setCurrentPage((prev) =>
                                Math.min(prev + 1, totalPages),)
                        }
                        className="disabled:opacity-40"
                    >
                        <FaAngleRight />
                    </button>
                    <button
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(totalPages)}
                        className="disabled:opacity-40"
                    >
                        <FaAngleDoubleRight />
                    </button>
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white p-8 rounded-lg shadow-lg w-[90%] sm:w-[400px]">
                        <h3 className="text-xl font-semibold mb-4">
                            {form.id ? "Edit Config" : "Create Config"}
                        </h3>
                        <form
                            onSubmit={handleSubmit}
                            className="flex flex-col gap-4"
                        >
                            <input
                                type="text"
                                name="field_name"
                                placeholder="Key"
                                value={form.field_name}
                                onChange={handleChange}
                                className="p-2 border rounded"
                                required
                                disabled={!!form.id}
                            />
                            <input
                                type="text"
                                name="field_value"
                                placeholder="Value"
                                value={form.field_value}
                                onChange={handleChange}
                                className="p-2 border rounded"
                                required
                            />
                            <textarea
                                name="var_data"
                                placeholder="Description"
                                value={form.var_data}
                                onChange={handleChange}
                                className="p-2 border rounded"
                                rows={3}
                            ></textarea>
                            <label>
                                <input
                                    type="checkbox"
                                    name="is_enabled"
                                    checked={form.is_enabled === 1}
                                    onChange={handleChange}
                                />
                                <span className="ml-2">Enabled</span>
                            </label>
                            <div className="flex justify-between">
                                <button
                                    type="button"
                                    className="bg-gray-300 px-4 py-2 rounded"
                                    onClick={() => setShowModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="bg-blue-600 text-white px-4 py-2 rounded"
                                >
                                    {form.id ? "Update" : "Create"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ConfigurationSettings;
